import {useEffect, FC, useState, useRef} from 'react';
import {RouteComponentProps, Prompt, generatePath} from 'react-router-dom';
import {observer} from 'mobx-react-lite';
import {runInAction, when} from 'mobx';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import EditIcon from '@material-ui/icons/Edit';
import {
    AppBar,
    Button,
    ClickAwayListener,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Grow,
    IconButton,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Tab,
    Tabs,
    Toolbar,
    Typography,
    Tooltip,
    makeStyles,
    CircularProgress,
    TextField,
    Box,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import LinkIcon from '@material-ui/icons/Link';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import {FormDataPanel} from './Forms';
import {SsnForm} from '@/APIs/types';
import {ContactsPanel} from '../Agent/Contacts/Contacts';
import RootStore from '../Core/mobx/RootStore';
import {AgentStore} from '../Agent/AgentStore';
import {CaseManagerStore} from './CaseManagerStore';
import {Alert, AlertTitle} from '@material-ui/lab';
import {IS_PRODUCTION} from '../constants';

interface TabParams {
    tabIndex?: string;
    historyFormId?: string;
    email?: string;
}
interface FormModalProps extends RouteComponentProps<TabParams> {
    store: AgentStore | CaseManagerStore;
}

export const TabMapping = {
    '0': 'prequal',
    '1': 'quote',
    '2': 'post-app-wrap',
    '3': 'preapproval',
    '4': 'underwriting',
    '5': 'banking',
    '6': 'policy',
} as const;

export type TabMappingValue = (typeof TabMapping)[keyof typeof TabMapping];

/** Resolves a URL tab index string to an assist/form tab id, or undefined if unknown. */
export function getTabMappingLabel(tabIndex: string): TabMappingValue | undefined {
    if (tabIndex in TabMapping) {
        return TabMapping[tabIndex as keyof typeof TabMapping];
    }
    return undefined;
}

// Date cutoff for validation behavior change
const VALIDATION_CUTOFF_DATE = new Date('2025-02-12T00:00:00');

const FORM_TYPE_MAPPING: Record<string, 'prequal' | 'quote' | 'preapproval'> = {
    '0': 'prequal',
    '1': 'quote',
    '2': 'preapproval',
};

const useStyles = makeStyles({
    disabledTab: {
        opacity: 0.5,
        cursor: 'not-allowed',
        '& .MuiTab-wrapper': {
            pointerEvents: 'none',
        },
    },
    tooltip: {
        fontSize: '14px',
        backgroundColor: 'rgba(0, 0, 0, 0.87)',
        padding: '8px 12px',
    },
    topToolbarStyle: {
        backgroundColor: '#e8edec',
        '& .MuiButton-root': {
            fontFamily: 'FredokaRegular',
            fontWeight: 600,
            fontSize: '12px',
            lineHeight: '140%',
            letterSpacing: '1.6px',
            backgroundColor: '#456d69',
            color: '#fff',
        },
        '& .MuiButton-root.Mui-disabled': {
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
            color: 'rgba(0, 0, 0, 0.26)',
        },
        '& .MuiButton-root.Mui-disabled:hover': {
            backgroundColor: '#456d69',
            color: '#fff',
        },

        '& .caseActionsButton': {
            backgroundColor: '#fff',
            color: '#456d69',
            border: '1px solid #456d69',
        },
        '& .MuiMenuItem-root': {
            fontFamily: 'FredokaRegular',
            fontWeight: 400,
            fontSize: '18px',
            lineHeight: '27px',
            color: '#174943',
            '&:hover': {
                backgroundColor: '#456d69',
                color: '#fff',
            },
        },
        '& .MuiMenuItem-root.Mui-disabled': {
            color: '#8e8e8e',
            opacity: 1,
        },
    },
    toolbarStyle: {
        padding: '1px 30px',
        backgroundColor: '#fff',
        color: '#456d69',
        '& .MuiTab-wrapper': {
            fontFamily: 'FredokaRegular',
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: '140%',
            letterSpacing: '1.6px',
        },
        '& .MuiTabs-indicator': {
            backgroundColor: '#456d69',
            marginBottom: '10px',
        },
    },
});

let resolveConfirmSaveFormModal: (confirm: boolean) => void;

const FormModal: FC<FormModalProps> = observer(({store, match, history}) => {
    const classes = useStyles();
    const {familyFormId, familyEmail} = Object.fromEntries(new URLSearchParams(location.search));
    const {params} = match;
    const {tabIndex = '0', historyFormId, email} = params;
    const {
        parameterStore: {formDirty},
        contactsFormStore,
    } = store;

    const [anchorMenu, setAnchorMenu] = useState<null | HTMLElement>(null);
    const [saveCaseLoading, setSaveCaseLoading] = useState(false);
    const [addFamilyPolicyLoading, setAddFamilyPolicyLoading] = useState(false);
    const [assignContactDialogOpen, setAssignContactDialogOpen] = useState(false);
    const [saveConfirmationOpen, setSaveConfirmationOpen] = useState(false);
    const [duplicateSsnForms, setDuplicateSsnForms] = useState<SsnForm[]>([]);
    const caseActionsAnchorRef = useRef<HTMLButtonElement>(null);
    const handleCaseActionsClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorMenu(event.currentTarget);
    };
    const handleCaseActionsClose = () => {
        setAnchorMenu(null);
    };
    const handleCaseActionsClickAway = (event: React.MouseEvent<Document> | React.TouchEvent<Document>) => {
        if (caseActionsAnchorRef.current && caseActionsAnchorRef.current.contains(event.target as Node)) {
            return;
        }
        setAnchorMenu(null);
    };
    const handleCaseActionsListKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setAnchorMenu(null);
        }
    };
    const caseActionsMenuOpen = Boolean(anchorMenu);
    const prevCaseActionsOpen = useRef(caseActionsMenuOpen);
    useEffect(() => {
        if (prevCaseActionsOpen.current === true && caseActionsMenuOpen === false) {
            caseActionsAnchorRef.current?.focus();
        }
        prevCaseActionsOpen.current = caseActionsMenuOpen;
    }, [caseActionsMenuOpen]);

    const [editIconMenuAnchor, setEditIconMenuAnchor] = useState<null | HTMLElement>(null);
    const editIconMenuOpen = Boolean(editIconMenuAnchor);
    const editIconAnchorRef = useRef<HTMLSpanElement>(null);
    const handleEditIconMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        if (editIconMenuAnchor) {
            handleEditIconMenuClose();
        } else {
            setEditIconMenuAnchor(event.currentTarget);
        }
    };
    const handleEditIconMenuClose = () => {
        setEditIconMenuAnchor(null);
    };
    const handleEditIconMenuClickAway = (event: React.MouseEvent<Document> | React.TouchEvent<Document>) => {
        if (editIconAnchorRef.current && editIconAnchorRef.current.contains(event.target as Node)) {
            return;
        }
        setEditIconMenuAnchor(null);
    };
    const dirty = Array.from(formDirty.values()).includes(true);
    const {customerPortalStore} = RootStore.agentPortalStore;
    const {parameterStore} = store;

    // Check if tabs should be disabled based on form creation date
    const shouldDisableTabs = (() => {
        // Check if this is a new form being created (most reliable check)
        const isNewForm = location.pathname.includes('/agent/newform');

        if (!isNewForm) {
            // This is an existing form being edited
            const hasFormCreatedAt =
                parameterStore.formCreatedAt !== null && parameterStore.formCreatedAt !== undefined;

            if (hasFormCreatedAt) {
                const formCreationDate = new Date(parameterStore.formCreatedAt as string | number | Date);
                if (formCreationDate < VALIDATION_CUTOFF_DATE) {
                    // Form created before Feb 12, 2025 - don't disable tabs
                    return false;
                }
                // Form has formCreatedAt and it's after cutoff - continue to check product selection
            } else {
                // No formCreatedAt - check updatedTimestamp to determine if it's an old or recently created form
                if (parameterStore.updatedTimestamp) {
                    const updatedDate = new Date(parameterStore.updatedTimestamp);
                    // If updated timestamp is recent (within 24 hours of cutoff), treat as new form
                    const dayAfterCutoff = new Date(VALIDATION_CUTOFF_DATE);
                    dayAfterCutoff.setDate(dayAfterCutoff.getDate() + 1);

                    if (updatedDate < VALIDATION_CUTOFF_DATE) {
                        // Form was last updated before cutoff - it's an old form
                        return false;
                    }
                    // Form was updated after cutoff - likely newly created, continue to check product selection
                } else {
                    // No timestamps at all - assume it's an old form
                    return false;
                }
            }
        }

        // For new forms or forms created after Feb 12, 2025:
        // Check if a product has been selected
        const hasSelected = parameterStore.prequal.hasSelectedProduct?.value;
        return hasSelected !== true;
    })();

    const handleClose = () => {
        const path = historyFormId ? window.location.pathname.split('/', 6).join('/') : '/';
        history.push(path);
    };
    const openCustomerPortalData = (email: string) => {
        window.open(`/case-manager/customer-portal-data/?email=${email}`);
    };
    const openCustomerFormHistory = () => {
        const currentPath = window.location.pathname;
        const splitPath = currentPath.split('/');
        splitPath[splitPath.length - 1] = 'history';
        history.push(splitPath.join('/'));
    };

    const openCustomerSelection = () => {
        runInAction(() => {
            customerPortalStore.dialogOpen = true;
        });
    };

    const confirmSave = async (): Promise<boolean> => {
        const response = await new Promise<boolean>((resolve) => {
            resolveConfirmSaveFormModal = resolve;
            setSaveConfirmationOpen(true);
        });
        setSaveConfirmationOpen(false);
        return response;
    };

    const confirmPendingSave = () => {
        resolveConfirmSaveFormModal(true);
    };

    const cancelPendingSave = () => {
        resolveConfirmSaveFormModal(false);
    };

    const saveForm = async () => {
        setSaveCaseLoading(true);
        const formsWithSameSsn =
            await RootStore.agentPortalStore.contactsFormStore.getSsnFormsAssociatedWithCurrentFormSsn();
        const duplicates = formsWithSameSsn.filter((form) => form.formId !== store.parameterStore.formid);
        if (duplicates.length > 0) {
            setDuplicateSsnForms(duplicates);
            if (!(await confirmSave())) {
                setSaveCaseLoading(false);
                return;
            }
        }
        const form = await contactsFormStore.saveForm(FORM_TYPE_MAPPING[tabIndex]);
        const newPath = generatePath('/agent/form/:email/:formId/:tabIndex', {
            email: form?.contact,
            formId: form?.formid,
            tabIndex,
        });
        window.history.replaceState(null, '', newPath);
        history.push(newPath);
        RootStore.uiStore.showSnackbar('Form successfully saved', 'success');
        setSaveCaseLoading(false);
    };

    const addFamilyPolicy = async () => {
        setAddFamilyPolicyLoading(true);
        try {
            const form = await contactsFormStore.saveForm(FORM_TYPE_MAPPING[tabIndex]);
            await contactsFormStore.startFamilyForm({
                id: form?.formid,
                email: form?.contact,
            });
            window.open(`/agent/newform/2?familyFormId=${form?.formid}&familyEmail=${form?.contact}`, '_blank');
            RootStore.uiStore.showSnackbar('Form successfully saved', 'success');
        } finally {
            setAddFamilyPolicyLoading(false);
        }
    };

    const historyFormTabs = [{ariaLabel: 'preapproval-panel', label: 'Pre-approvals'}];

    const formTabs = [
        {ariaLabel: 'prequal-panel', label: 'Pre-qualification'},
        {ariaLabel: 'quotes-panel', label: 'Quotes'},
        ...(!IS_PRODUCTION ? [{ariaLabel: 'post-app-wrap-panel', label: 'Post-app wrap'}] : []),
        ...[
            {ariaLabel: 'preapproval-panel', label: 'Pre-approvals'},
            {ariaLabel: 'underwriting-panel', label: 'underwriting requirements'},
            {ariaLabel: 'banking-panel', label: 'banking information'},
            {ariaLabel: 'policy-panel', label: 'Policy Information'},
        ],
    ];

    useEffect(() => {
        if ((!familyFormId || !familyEmail) && !store.contactsFormStore.contact && !email) {
            RootStore.uiStore.showSnackbar('Please select a client first.', 'warning');
            history.push('/agent');
        }
    }, []);

    useEffect(() => {
        // If the form is saved and the URL contains '/agent/newform', redirect to the form page
        return when(
            () => !!parameterStore.updatedTimestamp && location.pathname.includes('/agent/newform'),
            () => {
                const currentUrl = location.pathname;

                // Define the pattern to look for '/agent/newform' in the URL
                const pattern = /\/agent\/newform/;

                // Replace with '/agent/form/${contact}/${formid}'
                const newUrl = currentUrl.replace(
                    pattern,
                    `/agent/form/${parameterStore.contact}/${parameterStore.formid}`,
                );
                history.replace(newUrl);
            },
        );
    }, []);

    return (
        <>
            <Prompt
                when={dirty}
                message={(location, action) => {
                    const pathParams = location.pathname.split('/');
                    const lastParam = pathParams[pathParams.length - 1];

                    if (lastParam.length === 0) {
                        const message = {
                            confirmText: 'Stay',
                            messageText: `It looks like you might have some unsaved changes with ${Array.from(
                                formDirty.keys(),
                            ).join(',')} form! Are you sure you want to continue?`,
                            cancelText: 'Leave',
                        };
                        return JSON.stringify(message);
                    }
                    const tabLabel = getTabMappingLabel(tabIndex);
                    if (lastParam !== tabIndex && tabLabel && formDirty.get(tabLabel)) {
                        const message = {
                            confirmText: 'Stay',
                            messageText: `It looks like you might have some unsaved changes with ${tabLabel} form! Are you sure you want to continue?`,
                            cancelText: 'Leave',
                        };
                        return JSON.stringify(message);
                    }
                    return true;
                }}
            />
            <AppBar position="sticky">
                {!RootStore.currentAgentStates.includes(parameterStore.approval.state.value) && (
                    <Alert
                        severity="error"
                        style={{
                            backgroundColor: '#FF0000',
                            color: '#FFFFFF',
                        }}
                        iconMapping={{
                            error: <ErrorOutlineIcon style={{color: '#FFFFFF'}} />,
                        }}
                    >
                        <AlertTitle>Warning</AlertTitle>
                        <strong>
                            <>You are not licensed in the client's selected state!</>
                        </strong>
                    </Alert>
                )}
                <Toolbar className={classes.topToolbarStyle}>
                    <Typography
                        variant="h6"
                        style={{
                            marginLeft: 2,
                            flex: 1,
                            color: '#456d69',
                            paddingLeft: 26,
                            fontSize: 18,
                            lineHeight: '150%',
                        }}
                    >
                        Case:{' '}
                        <span
                            ref={editIconAnchorRef}
                            role="button"
                            tabIndex={0}
                            onClick={handleEditIconMenuClick}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    if (editIconMenuOpen) {
                                        handleEditIconMenuClose();
                                    } else if (editIconAnchorRef.current) {
                                        setEditIconMenuAnchor(editIconAnchorRef.current);
                                    }
                                }
                            }}
                            style={{
                                fontSize: 16,
                                marginBottom: 3,
                                cursor: 'pointer',
                                display: 'inline-flex',
                                verticalAlign: 'middle',
                            }}
                            aria-haspopup="true"
                            aria-controls={editIconMenuOpen ? 'edit-icon-menu' : undefined}
                        >
                            <EditIcon style={{fontSize: 16}} />
                        </span>{' '}
                        {store.contactsFormStore.name} ⦁{' '}
                        <span style={{color: '#8ba4a1'}}>Client: {store.contactsFormStore.contact}</span>
                    </Typography>
                    <Button
                        variant="contained"
                        disableElevation
                        disableRipple
                        style={{marginRight: '1em'}}
                        disabled={saveCaseLoading || !contactsFormStore.contact || !!historyFormId}
                        onClick={saveForm}
                    >
                        Save Case
                    </Button>
                    {historyFormId && (
                        <Button
                            variant="contained"
                            disableElevation
                            disableRipple
                            style={{marginRight: '1em'}}
                            disabled={saveCaseLoading}
                            onClick={async () => {
                                parameterStore.formid = '';
                                saveForm();
                            }}
                        >
                            Save as a form
                        </Button>
                    )}
                    <Button
                        ref={caseActionsAnchorRef}
                        variant="outlined"
                        disableElevation
                        disableRipple
                        className="caseActionsButton"
                        aria-controls={caseActionsMenuOpen ? 'case-actions-menu' : undefined}
                        aria-haspopup="true"
                        onClick={(e) => {
                            if (Boolean(anchorMenu)) handleCaseActionsClose();
                            else handleCaseActionsClick(e);
                        }}
                        endIcon={
                            <ArrowForwardIosIcon
                                style={{
                                    transition: 'transform 0.1s ease-in-out',
                                    transform: Boolean(anchorMenu) ? 'rotate(90deg)' : 'rotate(0deg)',
                                }}
                            />
                        }
                    >
                        Case Actions
                    </Button>
                    <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
                        <CloseIcon style={{color: '#456d69'}} />
                    </IconButton>
                    <Popper
                        open={editIconMenuOpen}
                        anchorEl={editIconMenuAnchor}
                        role={undefined}
                        transition
                        placement="bottom-start"
                        disablePortal
                        style={{zIndex: 1400}}
                    >
                        {({TransitionProps}) => (
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin: 'center top',
                                }}
                            >
                                <Paper
                                    style={{
                                        marginTop: 10,
                                        border: '1px solid #456d69',
                                    }}
                                >
                                    <ClickAwayListener onClickAway={handleEditIconMenuClickAway}>
                                        <Box
                                            style={{
                                                padding: 20,
                                                display: 'flex',
                                                flexDirection: 'row',
                                                gap: 20,
                                                width: 'fit-content',
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleEditIconMenuClose();
                                                }
                                            }}
                                        >
                                            <TextField
                                                autoFocus={editIconMenuOpen}
                                                variant="outlined"
                                                label="Case Name"
                                                value={contactsFormStore.name}
                                                onChange={(e) => {
                                                    runInAction(() => {
                                                        contactsFormStore.name = e.target.value;
                                                    });
                                                }}
                                            />
                                            <Button
                                                disabled={!!historyFormId}
                                                onClick={() => {
                                                    setAssignContactDialogOpen(true);
                                                }}
                                            >
                                                Assign Contact
                                            </Button>
                                        </Box>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                    <Popper
                        open={caseActionsMenuOpen}
                        anchorEl={anchorMenu}
                        role={undefined}
                        transition
                        placement="bottom-end"
                        disablePortal
                        style={{zIndex: 1400}}
                    >
                        {({TransitionProps, placement}) => (
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin: 'center top',
                                }}
                            >
                                <Paper
                                    style={{
                                        marginTop: 10,
                                        border: '1px solid #456d69',
                                    }}
                                >
                                    <ClickAwayListener onClickAway={handleCaseActionsClickAway}>
                                        <MenuList
                                            autoFocusItem={caseActionsMenuOpen}
                                            id="case-actions-menu"
                                            onKeyDown={handleCaseActionsListKeyDown}
                                        >
                                            <MenuItem disabled>Case Actions</MenuItem>
                                            <MenuItem
                                                disabled={
                                                    addFamilyPolicyLoading ||
                                                    !contactsFormStore.contact ||
                                                    !!historyFormId ||
                                                    !!familyEmail ||
                                                    !!familyFormId
                                                }
                                                onClick={async () => {
                                                    await addFamilyPolicy();
                                                    handleCaseActionsClose();
                                                }}
                                                style={{paddingLeft: 46}}
                                            >
                                                {addFamilyPolicyLoading && (
                                                    <CircularProgress
                                                        style={{
                                                            height: 14,
                                                            width: 14,
                                                            marginLeft: -30,
                                                            marginRight: 16,
                                                            color: 'inherit',
                                                        }}
                                                    />
                                                )}{' '}
                                                Add Family Policy
                                            </MenuItem>
                                            {!historyFormId && (
                                                <>
                                                    <MenuItem
                                                        onClick={() => {
                                                            handleCaseActionsClose();
                                                            openCustomerSelection();
                                                        }}
                                                    >
                                                        {customerPortalStore.selectedCustomer ? (
                                                            <LinkIcon
                                                                htmlColor="#0f0"
                                                                style={{width: 20, marginRight: 10}}
                                                            />
                                                        ) : (
                                                            <LinkOffIcon
                                                                htmlColor="#f00"
                                                                style={{width: 20, marginRight: 10}}
                                                            />
                                                        )}
                                                        Link Customer
                                                    </MenuItem>
                                                    <MenuItem
                                                        onClick={() => {
                                                            handleCaseActionsClose();
                                                            openCustomerPortalData(
                                                                encodeURIComponent(store.contactsFormStore.contact),
                                                            );
                                                        }}
                                                        style={{paddingLeft: 46}}
                                                    >
                                                        Data From Customer Portal
                                                    </MenuItem>
                                                    <MenuItem
                                                        onClick={() => {
                                                            handleCaseActionsClose();
                                                            openCustomerFormHistory();
                                                        }}
                                                        style={{paddingLeft: 46}}
                                                    >
                                                        View History
                                                    </MenuItem>
                                                </>
                                            )}
                                        </MenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                </Toolbar>
                <Tabs
                    className={classes.toolbarStyle}
                    value={parseInt(tabIndex, 10)}
                    centered
                    onChange={(_e, v) => {
                        // Prevent navigation to disabled tabs
                        // Tab indices: 0=prequal, 1=quotes, 2=post-app-wrap(non-prod)/preapproval(prod), 3+=others
                        if (shouldDisableTabs && v !== 0) {
                            return;
                        }
                        const newPath = generatePath(match.path, {
                            ...match.params,
                            tabIndex: historyFormId ? v + 2 : v,
                        });
                        history.push(newPath);
                    }}
                >
                    {historyFormId
                        ? historyFormTabs.map((tab, index) => (
                              <Tab aria-label={tab.ariaLabel} label={tab.label} key={index} />
                          ))
                        : formTabs.map((tab, index) => {
                              const isDisabled = shouldDisableTabs && index !== 0;
                              const tabElement = (
                                  <Tab
                                      aria-label={tab.ariaLabel}
                                      label={tab.label}
                                      key={index}
                                      disabled={isDisabled}
                                      className={isDisabled ? classes.disabledTab : ''}
                                  />
                              );

                              if (isDisabled) {
                                  return (
                                      <Tooltip
                                          key={index}
                                          title="Must select product from pre-qual tab"
                                          classes={{tooltip: classes.tooltip}}
                                          arrow
                                      >
                                          <span>{tabElement}</span>
                                      </Tooltip>
                                  );
                              }

                              return tabElement;
                          })}
                </Tabs>
            </AppBar>
            <Grid container style={{flex: 1}}>
                <Grid item xs={12} style={{display: 'flex', paddingTop: 10}}>
                    <FormDataPanel selectedTabIndex={parseInt(tabIndex, 10)} store={store} hideContactForm />
                </Grid>
            </Grid>
            <Dialog
                open={saveConfirmationOpen}
                onClose={() => cancelPendingSave()}
                aria-labelledby="confirm-dialog-title"
            >
                <DialogTitle id="confirm-dialog-title">Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText>The SSN is associated with following forms:</DialogContentText>
                    <DialogContentText>
                        <ul>
                            {duplicateSsnForms.map(({formId, email}) => (
                                <li key={formId}>
                                    {formId} - {email}
                                </li>
                            ))}
                        </ul>
                    </DialogContentText>
                    <DialogContentText>Please double check before confirming save.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => cancelPendingSave()} color="primary">
                        Cancel
                    </Button>
                    <Button color="primary" onClick={() => confirmPendingSave()}>
                        Confirm Save
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog fullScreen open={assignContactDialogOpen} onClose={() => setAssignContactDialogOpen(false)}>
                <AppBar style={{position: 'relative'}}>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={() => setAssignContactDialogOpen(false)}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography variant="h6" style={{marginLeft: 2, flex: 1}}>
                            Assign Contact
                        </Typography>
                        <Button autoFocus color="inherit" onClick={() => setAssignContactDialogOpen(false)}>
                            save
                        </Button>
                    </Toolbar>
                </AppBar>
                <Grid container spacing={3}>
                    <Grid item xs={12} style={{padding: 30}}>
                        <ContactsPanel
                            selectContact={(email) => {
                                setAssignContactDialogOpen(false);
                                runInAction(() => {
                                    contactsFormStore.contact = email;
                                    parameterStore.contact = email;
                                });
                            }}
                        />
                    </Grid>
                </Grid>
            </Dialog>
        </>
    );
});

FormModal.displayName = 'FormModal';

export default FormModal;
